import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Shield, FileDown, CheckCircle, Camera, MapPin, Calendar, DollarSign, ArrowLeft, LogOut } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: API, withCredentials: true });

const STATUS_COLORS = {
  'Client Data': 'bg-purple-50 text-purple-700', 'Appraisal Required': 'bg-purple-50 text-purple-700', 'Appraisal Booked': 'bg-blue-50 text-blue-700', 'Quoted': 'bg-amber-50 text-amber-700', 'Quote Sent': 'bg-orange-50 text-orange-700',
  'Accepted': 'bg-blue-50 text-blue-700', 'Work Booked': 'bg-cyan-50 text-cyan-700', 'Work In Progress': 'bg-teal-50 text-teal-700',
  'Completed': 'bg-green-50 text-green-700', 'Invoiced': 'bg-emerald-50 text-emerald-700', 'Paid': 'bg-green-100 text-green-800',
};

function PortalLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const { data } = await portalApi.post('/portal/login', { email, access_code: code });
      onLogin(data);
    } catch (e) { setError(e.response?.data?.detail || 'Login failed'); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6" data-testid="portal-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-lg bg-[#D35400] flex items-center justify-center mx-auto mb-4"><span className="text-white text-2xl font-extrabold">R</span></div>
          <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope]">Client Portal</h1>
          <img src="/rtr-wordmark.png" alt="Roofs To Remedy Ltd" className="mx-auto mt-2 h-6 w-auto max-w-[220px] object-contain" />
        </div>
        <div className="bg-white rounded-xl border border-[#E2E2DF] p-6 shadow-sm">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4 text-sm" data-testid="portal-login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Email</label><input data-testid="portal-email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" placeholder="Your email address" required /></div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Access Code</label>
              <div className="relative">
                <input data-testid="portal-code" type={showCode ? 'text' : 'password'} value={code} onChange={e => setCode(e.target.value)} className="w-full px-4 py-3 border border-[#E2E2DF] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400] pr-12" placeholder="Your access code" required />
                <button type="button" onClick={() => setShowCode(!showCode)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showCode ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <button data-testid="portal-login-button" type="submit" disabled={loading} className="w-full py-3 bg-[#D35400] hover:bg-[#A04000] text-white font-semibold rounded-lg transition-all disabled:opacity-50">{loading ? 'Signing in...' : 'Sign In'}</button>
          </form>
          <p className="text-xs text-slate-400 text-center mt-4">Your access code was provided by your roofing contractor.</p>
        </div>
      </div>
    </div>
  );
}

function PortalDashboard({ client, onLogout }) {
  const [appraisals, setAppraisals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [approveNote, setApproveNote] = useState('');

  useEffect(() => {
    portalApi.get('/portal/appraisals').then(r => { setAppraisals(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const loadDetail = async (id) => {
    setSelected(id);
    try {
      const { data } = await portalApi.get(`/portal/appraisals/${id}`);
      setDetail(data);
    } catch (e) { console.error(e); }
  };

  const downloadQuote = async (id) => {
    try {
      const response = await portalApi.get(`/portal/download-quote/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${id.slice(-6)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error(e); }
  };

  // Iter165 Phase B — customer-facing Remedial Repairs PDF. Only
  // exposed when the appraisal has at least one non-declined leak
  // area (detail.has_remedials, computed below in loadDetail).
  const downloadRemedials = async (id) => {
    try {
      const response = await portalApi.get(`/portal/download-remedials/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `RemedialRepairs_${id.slice(-6)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { console.error(e); }
  };

  const approveWork = async () => {
    setApproving(true);
    try {
      await portalApi.post(`/portal/appraisals/${selected}/approve`, { note: approveNote });
      await loadDetail(selected);
      setApproveNote('');
      const { data } = await portalApi.get('/portal/appraisals');
      setAppraisals(data || []);
    } catch (e) { alert(e.response?.data?.detail || 'Cannot approve'); }
    setApproving(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-slate-400">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#F9F9F8]" data-testid="portal-dashboard">
      <header className="bg-white border-b border-[#E2E2DF] px-4 lg:px-8 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-[#D35400] flex items-center justify-center"><span className="text-white text-sm font-extrabold">R</span></div>
            <div className="flex flex-col">
              <img src="/rtr-wordmark.png" alt="Roofs To Remedy Ltd" data-testid="portal-wordmark" className="block h-5 w-auto max-w-[170px] object-contain" />
              <p className="text-[10px] text-slate-500 mt-0.5">Client Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 hidden sm:block">Welcome, {client.name}</span>
            <button data-testid="portal-logout" onClick={onLogout} className="p-2 text-slate-400 hover:text-red-500"><LogOut size={18} /></button>
          </div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto p-4 lg:p-8">
        {!selected ? (
          <div className="space-y-4">
            <h2 className="text-2xl font-extrabold text-slate-900 font-[Manrope]">Your Projects</h2>
            <p className="text-sm text-slate-500">View your roof appraisals, quotes, and job progress.</p>
            {appraisals.length === 0 ? <p className="text-slate-400 py-8 text-center">No projects yet.</p> : (
              <div className="grid gap-4">
                {appraisals.map(a => (
                  <div key={a._id} data-testid={`portal-appraisal-${a._id}`} onClick={() => loadDetail(a._id)} className="bg-white border border-[#E2E2DF] rounded-lg p-5 hover:shadow-md hover:border-slate-300 cursor-pointer transition-all">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-lg font-bold text-slate-900">{a.quote_type_ref || 'Roof Appraisal'}</p>
                        {a.property_address && <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin size={14} />{a.property_address}</p>}
                      </div>
                      <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[a.appraisal_status] || 'bg-slate-100 text-slate-600'}`}>{a.appraisal_status}</span>
                    </div>
                    <div className="flex gap-6 mt-3 text-sm text-slate-500">
                      {a.appraisal_date && <span className="flex items-center gap-1"><Calendar size={13} />{new Date(a.appraisal_date).toLocaleDateString()}</span>}
                      {a.contract_amount > 0 && <span className="flex items-center gap-1"><DollarSign size={13} />${Number(a.contract_amount).toLocaleString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : detail ? (
          <div className="space-y-4">
            <button onClick={() => { setSelected(null); setDetail(null); }} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft size={16} /> Back to Projects</button>
            <div className="bg-white border border-[#E2E2DF] rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">{detail.quote_type_ref || 'Appraisal'}</h2>
                  {detail.property && <p className="text-sm text-slate-500 mt-1"><MapPin size={14} className="inline" /> {detail.property.property_address_num} {detail.property.property_address_street}, {detail.property.property_address_suburb}</p>}
                </div>
                <span className={`text-sm font-semibold px-3 py-1.5 rounded-full ${STATUS_COLORS[detail.appraisal_status] || 'bg-slate-100 text-slate-600'}`}>{detail.appraisal_status}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[['Status', detail.appraisal_status], ['Date', detail.appraisal_date ? new Date(detail.appraisal_date).toLocaleDateString() : '-'], ['Quote Total', detail.quote_total ? `$${Number(detail.quote_total).toLocaleString(undefined, {minimumFractionDigits: 2})}` : '-'], ['Contract', detail.contract_amount ? `$${Number(detail.contract_amount).toLocaleString()}` : '-']].map(([l, v]) => (
                  <div key={l} className="bg-slate-50 p-3 rounded"><span className="text-xs text-slate-500">{l}</span><p className="text-base font-bold text-slate-900">{v}</p></div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 mb-6">
                <button data-testid="portal-download-quote" onClick={() => downloadQuote(detail._id)} className="flex items-center gap-2 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold"><FileDown size={14} /> Download Quote PDF</button>
                {detail.has_remedials && (
                  <button data-testid="portal-download-remedials" onClick={() => downloadRemedials(detail._id)} className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-md text-sm font-semibold"><FileDown size={14} /> Download Remedial Repairs PDF</button>
                )}
                {(detail.appraisal_status === 'Quoted' || detail.appraisal_status === 'Quote Sent') && (
                  <div className="flex items-center gap-2">
                    <input value={approveNote} onChange={e => setApproveNote(e.target.value)} placeholder="Optional note..." className="px-3 py-2 border border-[#E2E2DF] rounded-md text-sm w-48" />
                    <button data-testid="portal-approve-work" onClick={approveWork} disabled={approving} className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-semibold disabled:opacity-50"><CheckCircle size={14} /> {approving ? 'Approving...' : 'Approve Work'}</button>
                  </div>
                )}
              </div>
              {detail.appraisal_comments && <div className="bg-slate-50 p-4 rounded-md mb-6"><p className="text-xs font-bold uppercase text-slate-500 mb-1">Comments</p><p className="text-sm text-slate-700">{detail.appraisal_comments}</p></div>}
            </div>
            {/* Quote Items */}
            {(detail.quote_items || []).length > 0 && (
              <div className="bg-white border border-[#E2E2DF] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] mb-4">Quotation Breakdown</h3>
                <table className="w-full text-sm"><thead><tr className="bg-slate-50">{['Item', 'Type', 'Qty', 'Amount'].map(h => <th key={h} className="text-left px-3 py-2 text-xs font-bold uppercase text-slate-500">{h}</th>)}</tr></thead>
                  <tbody>{detail.quote_items.map(qi => (
                    <tr key={qi._id} className="border-t border-[#E2E2DF]">
                      <td className="px-3 py-2 font-medium">{qi.item}</td>
                      <td className="px-3 py-2 text-slate-600">{qi.item_type}</td>
                      <td className="px-3 py-2">{qi.quantity}</td>
                      <td className="px-3 py-2 font-semibold">${Number((qi.materials || 0) + (qi.labour || 0)).toFixed(2)}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            {/* Photos */}
            {(detail.photos || []).length > 0 && (
              <div className="bg-white border border-[#E2E2DF] rounded-lg p-6">
                <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] flex items-center gap-2 mb-4"><Camera size={18} className="text-[#D35400]" /> Inspection Photos</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {detail.photos.map(p => (
                    <div key={p._id} className="rounded-lg overflow-hidden border border-[#E2E2DF]">
                      <div className="aspect-square"><img src={`${BACKEND_URL}${p.url}`} alt={p.description} className="w-full h-full object-cover" loading="lazy" /></div>
                      {p.description && <p className="text-xs text-slate-500 p-2">{p.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : <div className="text-center py-12 text-slate-400">Loading...</div>}
      </main>
    </div>
  );
}

export default function ClientPortalPage() {
  const [client, setClient] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    portalApi.get('/portal/me').then(r => setClient(r.data)).catch(() => setClient(null)).finally(() => setChecking(false));
  }, []);

  const handleLogout = async () => {
    await portalApi.post('/portal/logout').catch(() => {});
    setClient(null);
  };

  if (checking) return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]"><div className="animate-pulse text-slate-400">Loading...</div></div>;

  return client ? <PortalDashboard client={client} onLogout={handleLogout} /> : <PortalLogin onLogin={setClient} />;
}
